import React, { useMemo, useState } from 'react';
import { addDays, format } from 'date-fns';
import { Box, Button, Typography, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import MainCalendar from '../components/Calendario/MainCalendar';
import { useServer } from '../context/ServerContext';
import { fetchLogs } from '../utils/apiServer';

const panelSx = {
  border: '0.01vw solid rgba(233, 78, 26, 0.24)',
  borderRadius: '1vw',
  background:
    'radial-gradient(circle at top right, rgba(233, 78, 26, 0.12), transparent 24%), linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
  boxShadow: 'inset 0 0 0 0.01vw rgba(255,255,255,0.03)',
};

const filterControlSx = (focused) => ({
  height: '4.1vh',
  borderRadius: '0.8vw',
  border: `0.01vw solid ${focused ? 'rgba(233, 78, 26, 0.9)' : 'rgba(233, 78, 26, 0.28)'}`,
  background: 'rgba(255,255,255,0.03)',
  color: '#d8e0ea',
  outline: 'none',
  fontSize: '0.72vw',
  padding: '0 0.8vw',
  boxSizing: 'border-box',
});

const thSx = {
  textAlign: 'left',
  borderBottom: '0.01vw solid rgba(233, 78, 26, 0.22)',
  padding: '0.85vh 0.8vw',
  fontSize: '0.68vw',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#8fa0b5',
  position: 'sticky',
  top: 0,
  background: 'rgba(8, 14, 24, 0.98)',
  backdropFilter: 'blur(0.3vw)',
  zIndex: 1,
};

const tdSx = {
  padding: '0.95vh 0.8vw',
  borderBottom: '0.01vw solid rgba(233, 78, 26, 0.12)',
  fontSize: '0.72vw',
  color: '#d4dde8',
  verticalAlign: 'top',
};

const LogsUsuarios = ({ translate }) => {
  const t = (key, fallback) => {
    const value = typeof translate === 'function' ? translate(key) : key;
    return value && value !== key ? value : fallback;
  };

  const { servidor } = useServer();
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [state, setState] = useState([
    { startDate: addDays(new Date(), -7), endDate: new Date(), key: 'selection' },
  ]);

  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const startPreview = state?.[0]?.startDate ? new Date(state[0].startDate) : null;
  const endPreview = state?.[0]?.endDate ? new Date(state[0].endDate) : null;
  const rangeStart = startPreview && endPreview && startPreview > endPreview ? endPreview : startPreview;
  const rangeEnd = startPreview && endPreview && startPreview > endPreview ? startPreview : endPreview;
  const rangeDays = rangeStart && rangeEnd ? Math.floor((rangeEnd - rangeStart) / 86400000) + 1 : 0;
  const rangeLabel =
    rangeStart && rangeEnd
      ? `${rangeStart.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })} - ${rangeEnd.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}`
      : t('no_range_selected', 'Sin rango seleccionado');



  const [inputFocused, setInputFocused] = useState(false);
  const [typeFocused, setTypeFocused] = useState(false);
  const [userFocused, setUserFocused] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');

  const handleFetchClick = async () => {
    const { startDate, endDate } = state[0];
    const start = format(startDate, 'yyyy-MM-dd');
    const end = format(endDate, 'yyyy-MM-dd');

    try {
      const data = await fetchLogs(servidor, start, end);
      setLogs(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      setLogs([]);
    }
  };

  const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const getHighlightedText = (text, highlight) => {
    if (!highlight) return text;
    const regex = new RegExp(`(${escapeRegExp(highlight)})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ color: '#ff9c6a', fontWeight: 700 }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const logTypes = useMemo(
    () => ['all', ...Array.from(new Set(logs.map((log) => log.type)))],
    [logs]
  );

  const logUsers = useMemo(
    () => ['all', ...Array.from(new Set(logs.map((log) => log.username || log.usuario)))],
    [logs]
  );

  const filteredLogs = useMemo(
    () =>
      logs.filter((log) => {
        const textMatch = String(log.msg || '')
          .toLowerCase()
          .includes(searchText.toLowerCase());
        const typeMatch = typeFilter === 'all' || log.type === typeFilter;
        const userMatch = userFilter === 'all' || (log.username || log.usuario) === userFilter;
        return textMatch && typeMatch && userMatch;
      }),
    [logs, searchText, typeFilter, userFilter]
  );

  const startLabel = state?.[0]?.startDate ? format(state[0].startDate, 'dd/MM/yyyy') : '--';
  const endLabel = state?.[0]?.endDate ? format(state[0].endDate, 'dd/MM/yyyy') : '--';

  const summaryCards = [
    { label: t('logs_total', 'Total logs'), value: logs.length },
    { label: t('logs_filtered', 'Filtered'), value: filteredLogs.length },
    { label: t('logs_types', 'Types'), value: Math.max(logTypes.length - 1, 0) },
  ];

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', pr: '0.2vw' }}>
      <Box
        sx={{
          display: 'grid',
          gap: '1vw',
          gridTemplateColumns: '40vw 38vw',
          alignItems: 'stretch',
          mb: '1vw',
        }}
      >
        <Box sx={{ ...panelSx, p: '1vw' }}>
          <Typography sx={{ fontSize: '0.58vw', color: '#e94e1a', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            {t('history_control_title', 'User activity')}
          </Typography>
          <Typography sx={{ mt: '0.35vh', fontSize: '1.25vw', fontWeight: 700, color: '#f2f5f8' }}>
            {t('logs_users', 'Logs de Usuarios')}
          </Typography>
          <Typography sx={{ mt: '0.7vh', fontSize: '0.72vw', color: '#97a6b7', lineHeight: 1.6 }}>
            {t('logs_description', 'Consulta actividad por rango de fechas, busca mensajes y filtra por tipo o usuario.')}
          </Typography>

          <Box sx={{ mt: '1.1vw', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.6vw' }}>
            {summaryCards.map((item) => (
              <Box
                key={item.label}
                sx={{
                  borderRadius: '0.9vw',
                  border: '0.01vw solid rgba(233,78,26,0.18)',
                  background: 'rgba(255,255,255,0.03)',
                  px: '0.8vw',
                  py: '0.9vh',
                }}
              >
                <Typography sx={{ fontSize: '0.54vw', color: '#8fa0b5', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {item.label}
                </Typography>
                <Typography sx={{ mt: '0.35vh', fontSize: '1.2vw', fontWeight: 700, color: '#ffffff' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ ...panelSx, p: '1vw', display: 'flex', flexDirection: 'column', gap: '0.9vw' }}>
            <Box sx={rangeSummaryPanelSx}>
              <Box sx={rangeSummaryMainSx}>
                <Typography sx={stepKickerSx}>{t('temporal_range', 'Rango temporal')}</Typography>
                <Typography sx={rangeSummaryTitleSx}>{t('calendar', 'Calendario')}</Typography>
                <Typography sx={rangeSummaryTextSx}>{rangeLabel}</Typography>
              </Box>
              <Box sx={rangeSummarySideSx}>
                <Box sx={summaryDaysCardSx}>
                  <Typography sx={summaryLabelSx}>{t('days', 'Dias')}</Typography>
                  <Typography sx={summaryDaysValueSx}>{rangeDays}</Typography>
                </Box>
                <IconButton onClick={() => setCalendarModalOpen(true)} sx={calendarOpenButtonSx}>
                  <CalendarMonthIcon />
                </IconButton>
              </Box>
              <Button
                onClick={handleFetchClick}
                sx={{
                  borderRadius: '0.9vw',
                  px: '1vw',
                  minHeight: '4.1vh',
                  textTransform: 'none',
                  fontSize: '0.78vw',
                  fontWeight: 700,
                  color: '#ffffff',
                  background: 'linear-gradient(135deg, #ff6a2f, #e84e0e)',
                  boxShadow: '0 0.4vw 0.9vw rgba(232, 78, 14, 0.22)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ff7d4c, #f05f26)',
                  },
                  marginLeft:'6.45vw'
                }}
              >
                {t('fetch_logs', 'Obtener Logs')}
              </Button>
            </Box>
            {/* <MainCalendar state={state} setState={setState} /> */}

            <Modal open={calendarModalOpen} onClose={() => setCalendarModalOpen(false)}>
              <Box sx={calendarModalSx}>
                <Box sx={calendarModalHeaderSx}>
                  <Box>
                    <Typography sx={stepKickerSx}>{t('temporal_range', 'Rango temporal')}</Typography>
                    <Typography sx={sectionHeadingSx}>{t('calendar', 'Calendario')}</Typography>
                  </Box>
                  <IconButton onClick={() => setCalendarModalOpen(false)} sx={calendarModalCloseSx}>
                    <CloseIcon />
                  </IconButton>
                </Box>
    
                <MainCalendar
                  state={state}
                  setState={setState}
                  translate={translate}
                  // variant="config"
                  hideToolbar
                />
              </Box>
            </Modal>
          </Box>
      </Box>

      <Box sx={{ ...panelSx, p: '1vw' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 0.7fr 0.7fr',
            gap: '0.8vw',
            alignItems: 'center',
            mb: '0.9vw',
          }}
        >
          <input
            type="text"
            placeholder={t('search_message', 'Buscar mensaje...')}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            style={filterControlSx(inputFocused)}
          />

          <select
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            onFocus={() => setTypeFocused(true)}
            onBlur={() => setTypeFocused(false)}
            style={filterControlSx(typeFocused)}
          >
            {logTypes.map((type) => (
              <option key={type} value={type} style={{ backgroundColor: '#131c2a', color: '#d8e0ea' }}>
                {type}
              </option>
            ))}
          </select>

          <select
            value={userFilter}
            onChange={(event) => setUserFilter(event.target.value)}
            onFocus={() => setUserFocused(true)}
            onBlur={() => setUserFocused(false)}
            style={filterControlSx(userFocused)}
          >
            {logUsers.map((user) => (
              <option key={user} value={user} style={{ backgroundColor: '#131c2a', color: '#d8e0ea' }}>
                {user}
              </option>
            ))}
          </select>
        </Box>

        {error && (
          <Typography sx={{ mb: '0.8vw', color: '#ff7d7d', fontSize: '0.78vw', fontWeight: 600 }}>
            Error: {error}
          </Typography>
        )}

        <Box
          sx={{
            maxHeight: '36vh',
            overflow: 'auto',
            borderRadius: '0.9vw',
            border: '0.01vw solid rgba(233,78,26,0.16)',
            background: 'rgba(0,0,0,0.12)',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ ...thSx, width: '12%' }}>{t('user', 'Usuario')}</th>
                <th style={{ ...thSx, width: '14%' }}>{t('type', 'Tipo')}</th>
                <th style={{ ...thSx, width: '18%' }}>{t('date', 'Fecha')}</th>
                <th style={{ ...thSx, width: '56%' }}>{t('message', 'Mensaje')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log, index) => (
                <tr key={`${log.date}-${index}`}>
                  <td style={tdSx}>{log.username || log.usuario}</td>
                  <td style={tdSx}>{log.type}</td>
                  <td style={tdSx}>{new Date(log.date).toLocaleString()}</td>
                  <td style={{ ...tdSx, lineHeight: 1.55 }}>
                    {getHighlightedText(log.msg, searchText)}
                  </td>
                </tr>
              ))}

              {filteredLogs.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ ...tdSx, textAlign: 'center', padding: '2.2vh 1vw', color: '#8fa0b5' }}>
                    {t('no_matching_logs', 'No hay registros que coincidan.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </Box>
    </Box>
  );
};

export default LogsUsuarios;


const stepKickerSx = { fontSize: '0.48vw', color: '#e94e1a', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' };
const sectionHeadingSx = { mt: '0.12vh', fontSize: '1.05vw', fontWeight: 700, color: '#f4f7fb', lineHeight: 1.15 };
const rangeSummaryPanelSx = { display: 'grid', gridTemplateColumns: '29.5vw 5vw', gap: '0.8vw',alignItems: 'stretch', };

const rangeSummaryMainSx = {
  border: '0.01vw solid rgba(233, 78, 26, 0.24)',
  borderRadius: '1vw',
  px: '1vw',
  py: '0.95vw',
  background: 'radial-gradient(circle at top right, rgba(233, 78, 26, 0.12), transparent 30%), linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
  minWidth: 0,
};
const summaryDaysValueSx = { mt: '0.12vh', fontSize: '1.5vw', fontWeight: 800, color: '#ffffff', lineHeight: 1 };
const summaryLabelSx = { fontSize: '0.48vw', color: '#8ea1b5', textTransform: 'uppercase', letterSpacing: '0.08em' };
const rangeSummaryTitleSx = { mt: '0.22vh', fontSize: '1.05vw', fontWeight: 700, color: '#f4f7fb', lineHeight: 1.1 };
const rangeSummaryTextSx = { mt: '0.55vh', fontSize: '0.74vw', color: '#b8c5d4', lineHeight: 1.35 };
const rangeSummarySideSx = { display: 'grid', gridTemplateRows: '1fr auto', gap: '0.6vw', minWidth: '5.6vw' };
const summaryDaysCardSx = {
  border: '0.01vw solid rgba(233, 78, 26, 0.24)',
  borderRadius: '1vw',
  px: '0.85vw',
  py: '0.8vw',
  background: 'linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
};

const calendarOpenButtonSx = {
  borderRadius: '0.9vw',
  border: '0.01vw solid rgba(233, 78, 26, 0.24)',
  background: 'linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
  width: '100%',
  minHeight: '3.4vh',
  '&:hover': {
    background: 'rgba(233, 78, 26, 0.12)',
    borderColor: 'rgba(233, 78, 26, 0.5)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.05vw',
  },
};

const calendarModalSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(50vw, 100vh)',
  maxHeight: '82vh',
  overflow: 'auto',
  borderRadius: '1.1vw',
  border: '0.01vw solid rgba(233, 78, 26, 0.28)',
  background: 'radial-gradient(circle at top right, rgba(233, 78, 26, 0.12), transparent 24%), linear-gradient(180deg, rgba(12, 21, 34, 0.98), rgba(7, 14, 25, 0.98))',
  boxShadow: '0 1vw 2.6vw rgba(0,0,0,0.34)',
  p: '1vw',
  boxSizing: 'border-box',
};
const calendarModalHeaderSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '1vw',
  mb: '0.8vw',
};
const calendarModalCloseSx = {
  border: '0.01vw solid rgba(233, 78, 26, 0.24)',
  borderRadius: '0.9vw',
  color: '#dbe3ed',
  background: 'rgba(255,255,255,0.03)',
};
